import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import AgGridSkeleton from "@components/ag-grid/AgGridSkeleton";
import CopyButton from "@components/buttons/CopyButton";
import TransactionStatusClass from "@components/classes/TransactionStatusClass";
import GlobalSearchInputField from "@components/inputs/GlobalSearchInputField";
import DataPill from "@components/pills/DataPill";
import { transactionApiServices } from "@services/apis/Transactions";
import {
  GetUnifiedTransactionsResponse,
  UnifiedTransactionData,
} from "@services/models/Transactions/GetUnifiedTransactionsModel";
import { ColDef, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { isPaysonicHost } from "src/config/deploymentConfig";
import ConstantFunctions from "../../../utils/constants_functions";
import { formatDisplayDate, parseDisplayDate } from "../../../utils/safeDate";
import { StatusPillRenderer } from "../cpg/components/StatusPillRenderer";
import ConfirmClearComplianceModal from "./ConfirmClearComplianceModal";
import UnifiedTransactionTabs from "./UnifiedTransactionTabs";

const TIME_COLUMN_ID = "transactionTime";

function formatTransactionTime(
  value: string | null | undefined,
  useUtc: boolean,
): string {
  const date = parseDisplayDate(value);
  if (!date) return "";
  return formatDisplayDate(date, "en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZone: useUtc ? "UTC" : undefined,
  });
}

type TimeHeaderProps = {
  useUtc?: boolean;
  onToggle?: () => void;
};

const TimeColumnHeader = ({ useUtc = false, onToggle }: TimeHeaderProps) => (
  <span className="inline-flex items-center gap-1">
    <span>Time</span>
    <button
      type="button"
      title={useUtc ? "Switch to local time" : "Switch to UTC"}
      aria-label={useUtc ? "Switch to local time" : "Switch to UTC"}
      className="inline-flex items-center gap-0.5 cursor-pointer"
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onToggle?.();
      }}
    >
      <span className="text-[9px] font-semibold uppercase text-text-secondary">
        {useUtc ? "UTC" : "Local"}
      </span>
      <span className="material-symbols-outlined text-[12px] leading-none text-icon-base">
        sync_alt
      </span>
    </button>
  </span>
);

type TimeCellProps = {
  value?: string;
  data?: { date?: string };
  useUtc?: boolean;
  onToggle?: () => void;
};

const TimeCell = ({ value, data, useUtc = false, onToggle }: TimeCellProps) => {
  const text = formatTransactionTime(value || data?.date, useUtc);

  return (
    <button
      type="button"
      title={useUtc ? "Switch to local time" : "Switch to UTC"}
      aria-label={useUtc ? "Switch to local time" : "Switch to UTC"}
      className="flex items-center gap-1 h-full cursor-pointer"
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onToggle?.();
      }}
    >
      <span className="text-[10px] text-text-base font-medium">
        {text || "-"}
      </span>
      <span className="text-[9px] font-semibold uppercase text-text-secondary">
        {useUtc ? "UTC" : "Local"}
      </span>
      <span className="material-symbols-outlined text-[12px] leading-none text-icon-base">
        sync_alt
      </span>
    </button>
  );
};

type UnifiedTransactionsProps = {
  scope?: "external" | "internal";
};

const UnifiedTransactions = ({ scope = "external" }: UnifiedTransactionsProps) => {
  const incomingTransactionType =
    scope === "internal" ? "internal_incoming" : "external_incoming";
  const outgoingTransactionType =
    scope === "internal" ? "internal_outgoing" : "external_outgoing";
  const pageTitle =
    scope === "internal" ? "Internal Transactions" : "Transactions";
  const constantFunctions = new ConstantFunctions();
  const transactionStatusClass = new TransactionStatusClass();
  const isPaysonic = isPaysonicHost();

  const incomingGridRef = useRef<AgGridReact>(null);
  const outgoingGridRef = useRef<AgGridReact>(null);
  const incomingLoadingRef = useRef<boolean>(false);
  const outgoingLoadingRef = useRef<boolean>(false);

  const [incomingData, setIncomingData] = useState<UnifiedTransactionData[]>(
    [],
  );
  const [outgoingData, setOutgoingData] = useState<UnifiedTransactionData[]>(
    [],
  );
  const [incomingLoading, setIncomingLoading] = useState<boolean>(false);
  const [outgoingLoading, setOutgoingLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [incomingPage, setIncomingPage] = useState<number>(1);
  const [outgoingPage, setOutgoingPage] = useState<number>(1);
  const [incomingHasMore, setIncomingHasMore] = useState<boolean>(true);
  const [outgoingHasMore, setOutgoingHasMore] = useState<boolean>(true);
  const rowsPerPage = 1000;
  const [incomingSearchTerm, setIncomingSearchTerm] = useState<string>("");
  const [outgoingSearchTerm, setOutgoingSearchTerm] = useState<string>("");
  const [appliedIncomingSearchTerm, setAppliedIncomingSearchTerm] =
    useState<string>("");
  const [appliedOutgoingSearchTerm, setAppliedOutgoingSearchTerm] =
    useState<string>("");

  const [isPanelCollapsed, setIsPanelCollapsed] = useState<boolean>(false);
  const [selectedTransactionId, setSelectedTransactionId] = useState<number>();
  const [selectedTransactionData, setSelectedTransactionData] =
    useState<UnifiedTransactionData>();
  const [incomingPendingFilter, setIncomingPendingFilter] =
    useState<boolean>(false);
  const [outgoingPendingFilter, setOutgoingPendingFilter] =
    useState<boolean>(false);
  const [incomingCompliancePendingFilter, setIncomingCompliancePendingFilter] =
    useState<boolean>(false);
  const [outgoingCompliancePendingFilter, setOutgoingCompliancePendingFilter] =
    useState<boolean>(false);
  const [clearComplianceTransactionId, setClearComplianceTransactionId] =
    useState<number | null>(null);
  const [useUtcTime, setUseUtcTime] = useState(false);
  const toggleTimeZone = useCallback(() => {
    setUseUtcTime((current) => !current);
  }, []);

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Date",
        field: "date",
        flex: 0.8,
        minWidth: 90,
        maxWidth: 120,
        valueFormatter: (params) =>
          constantFunctions.dateFormatInDDMMYY(params.value),
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agDateColumnFilter",
      },
      {
        colId: TIME_COLUMN_ID,
        headerName: "Time",
        field: "createdAt",
        flex: 1,
        minWidth: 110,
        maxWidth: 160,
        sortable: true,
        filter: false,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        headerTooltip: useUtcTime
          ? "Showing UTC. Click to switch to local time."
          : "Showing local time. Click to switch to UTC.",
        headerComponentParams: {
          innerHeaderComponent: TimeColumnHeader,
          innerHeaderComponentParams: {
            useUtc: useUtcTime,
            onToggle: toggleTimeZone,
          },
        },
        cellRenderer: TimeCell,
        cellRendererParams: {
          useUtc: useUtcTime,
          onToggle: toggleTimeZone,
        },
      },
      {
        headerName: "Trx ID",
        field: "transactionId",
        flex: 0.8,
        minWidth: 110,
        maxWidth: 150,
        cellRenderer: (params: any) => {
          const { data } = params;
          const compliancePending =
            data?.compliancePending != null &&
            Number(data.compliancePending) !== 0;
          return (
            <div className="flex items-center justify-between h-full gap-1">
              <span className="text-[10px] text-text-base font-medium truncate">
                {data.transactionId}
              </span>
              <div className="flex items-center gap-1 shrink-0">
                {compliancePending && (
                  <button
                    type="button"
                    aria-label="Clear compliance"
                    title="Clear compliance"
                    className="flex justify-center items-center cursor-pointer bg-bg-status-yellow text-text-base"
                    style={{
                      width: "14px",
                      height: "14px",
                      borderRadius: "2px",
                    }}
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => {
                      event.stopPropagation();
                      setClearComplianceTransactionId(data.transactionId);
                    }}
                  >
                    <span
                      className="material-symbols-outlined leading-none"
                      style={{ fontSize: "10px" }}
                    >
                      policy
                    </span>
                  </button>
                )}
                <CopyButton
                  copyText={data.transactionId.toString()}
                  copyMessage="Transaction ID copied!"
                  copyIconSize={10}
                  buttonSize="14px"
                  borderRadius="2px"
                />
              </div>
            </div>
          );
        },
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Category",
        field: "userAccountType",
        minWidth: 120,
        flex: 1,
        sortable: true,
        cellRenderer: ({ data }: any) => (
          <DataPill value={data?.userAccountType} />
        ),
      },
      {
        headerName: "Account Type",
        field: "accountType",
        flex: 0.9,
        minWidth: 120,
        maxWidth: 140,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: ({ data }: any) => (
          <DataPill value={data?.accountType} />
        ),
      },
      {
        headerName: "Email",
        field: "userEmail",
        flex: 1.5,
        minWidth: 160,
        maxWidth: 220,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        wrapText: true,
        autoHeight: true,
        cellStyle: {
          whiteSpace: "normal",
          wordBreak: "break-word",
          lineHeight: "1.2",
        },
      },
      {
        headerName: "Name",
        field: "name",
        flex: 1.2,
        minWidth: 140,
        maxWidth: 180,
        valueGetter: (params) => {
          const { firstName, lastName } = params.data;
          return `${firstName || ""} ${lastName || ""}`.trim() || "N/A";
        },
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        wrapText: true,
        autoHeight: true,
        cellStyle: {
          whiteSpace: "normal",
          wordBreak: "break-word",
          lineHeight: "1.2",
        },
      },
      {
        headerName: "DBA",
        field: "dba",
        flex: 1.2,
        minWidth: 120,
        maxWidth: 160,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        wrapText: true,
        autoHeight: true,
        cellStyle: {
          whiteSpace: "normal",
          wordBreak: "break-word",
          lineHeight: "1.2",
        },
      },

      {
        headerName: "Merchant",
        field: "merchantName",
        flex: 1.2,
        minWidth: 120,
        maxWidth: 160,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        wrapText: true,
        autoHeight: true,
        cellStyle: {
          whiteSpace: "normal",
          wordBreak: "break-word",
          lineHeight: "1.2",
        },
      },
      {
        headerName: "Table",
        field: "transactionType",
        flex: 1.2,
        minWidth: 130,
        maxWidth: 180,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        wrapText: true,
        autoHeight: true,
        cellStyle: {
          whiteSpace: "normal",
          wordBreak: "break-word",
          lineHeight: "1.2",
        },
        cellRenderer: ({ data }: any) => {
          let table = "";
          if (
            data.transactionType?.toLowerCase() === "personal payin" ||
            data.transactionType?.toLowerCase() === "personal payout"
          ) {
            table = "Personal";
          } else if (
            data.transactionType?.toLowerCase() === "merchant payin" ||
            data.transactionType?.toLowerCase() === "merchant payout"
          ) {
            table = "Merchant";
          }
          const categoryStyle = transactionStatusClass.getTransactionStatusIcon(
            table.toLowerCase(),
          );
          return (
            <div
              className={`inline-flex h-[20px] items-center justify-center gap-1 whitespace-nowrap rounded px-[6px] text-[10px] font-medium ${categoryStyle.bgColor}`}
              style={{ color: categoryStyle.textColor }}
            >
              {table.toUpperCase() || "-"}
            </div>
          );
        },
      },
      {
        headerName: "Network",
        field: "network",
        flex: 0.8,
        minWidth: 90,
        maxWidth: 120,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellStyle: () => ({
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
        }),
        cellRenderer: (params: any) => {
          const { data } = params;

          return (
            <div className="flex flex-row gap-1 items-center justify-start w-full">
              <img
                src={data.networkImage}
                alt={data.network}
                className="w-[12px] h-[12px] rounded"
              />
              <span className="text-[10px] text-text-base font-medium">
                {data.network}
              </span>
            </div>
          );
        },
      },
      {
        headerName: "Base Amt",
        field: "baseAmount",
        flex: 0.8,
        minWidth: 110,
        maxWidth: 150,
        cellRenderer: (params: any) => {
          const { data } = params;
          return (
            <div className="flex flex-row gap-1 items-center justify-start">
              <img
                src={data.baseCurrencyImage}
                alt={data.baseCurrency}
                className="w-[12px] h-[12px] rounded"
              />
              <span className="text-[10px] text-text-base font-medium">
                {constantFunctions.formatCurrency(data.baseAmount)}
              </span>
            </div>
          );
        },
        cellStyle: () => ({
          display: "flex",
          justifyContent: "start",
          alignItems: "center",
        }),
        // headerClass: "ag-header-cell-custom header-center",
        cellClass: "ag-cell-custom",
        headerClass: "ag-header-cell-custom header-left",
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Req Amt",
        field: "requestedAmount",
        flex: 0.8,
        minWidth: 100,
        maxWidth: 140,
        cellRenderer: (params: any) => {
          const { data } = params;
          return (
            <div className="flex flex-row gap-1 items-center justify-start">
              <img
                src={data.requestedCurrencyImage}
                alt={data.requestedCurrency}
                className="w-[12px] h-[12px] rounded"
              />
              <span className="text-[10px] text-text-base font-medium">
                {constantFunctions.formatCurrency(data.requestedAmount)}
              </span>
            </div>
          );
        },
        cellStyle: () => ({
          display: "flex",
          justifyContent: "start",
          alignItems: "center",
        }),
        headerClass: "ag-header-cell-custom header-left",
        filter: "agNumberColumnFilter",
        cellClass: "ag-cell-custom",
      },
      {
        headerName: "Rec Amt",
        field: "receivedAmount",
        flex: 0.8,
        minWidth: 100,
        maxWidth: 140,
        cellRenderer: (params: any) => {
          const { data } = params;
          return (
            <div className="flex flex-row gap-1 items-center justify-start">
              <img
                src={data.requestedCurrencyImage}
                alt={data.requestedCurrency}
                className="w-[12px] h-[12px] rounded"
              />
              <span className="text-[10px] text-text-base font-medium">
                {constantFunctions.formatCurrency(data.receivedAmount)}
              </span>
            </div>
          );
        },
        cellStyle: () => ({
          display: "flex",
          justifyContent: "start",
          alignItems: "center",
        }),
        headerClass: "ag-header-cell-custom header-left",
        filter: "agNumberColumnFilter",
        cellClass: "ag-cell-custom",
      },
      {
        headerName: "Compliance",
        field: "compliancePending",
        flex: 1,
        minWidth: 100,
        maxWidth: 160,
        sortable: false,
        filter: false,
        cellRenderer: StatusPillRenderer,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        cellStyle: () => ({
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }),
      },
      {
        headerName: "Pending",
        field: "pending",
        flex: 0.7,
        minWidth: 80,
        maxWidth: 110,
        cellRenderer: StatusPillRenderer,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        sortable: false,
        filter: false,
        cellStyle: () => ({
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }),
      },
      {
        headerName: "Over",
        field: "overPayment",
        flex: 0.5,
        minWidth: 65,
        maxWidth: 90,
        cellRenderer: StatusPillRenderer,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        sortable: false,
        filter: false,
        cellStyle: () => ({
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }),
      },
      {
        headerName: "Under",
        field: "underPayment",
        flex: 0.6,
        minWidth: 75,
        maxWidth: 100,
        cellRenderer: StatusPillRenderer,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        sortable: false,
        filter: false,
        cellStyle: () => ({
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }),
      },
      {
        headerName: "Paid",
        field: "paid",
        flex: 0.6,
        minWidth: 70,
        maxWidth: 90,
        cellRenderer: StatusPillRenderer,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        sortable: false,
        filter: false,
        cellStyle: () => ({
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }),
      },
      {
        headerName: "Cancelled",
        field: "cancelled",
        flex: 0.8,
        minWidth: 90,
        maxWidth: 110,
        cellRenderer: StatusPillRenderer,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        sortable: false,
        filter: false,
        cellStyle: () => ({
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }),
      },
      {
        headerName: "Expired",
        field: "expired",
        flex: 0.7,
        minWidth: 80,
        maxWidth: 110,
        cellRenderer: StatusPillRenderer,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        sortable: false,
        filter: false,
        cellStyle: () => ({
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }),
      },
    ],
    [constantFunctions, toggleTimeZone, useUtcTime],
  );

  useEffect(() => {
    incomingGridRef.current?.api?.refreshCells({
      columns: [TIME_COLUMN_ID],
      force: true,
    });
    outgoingGridRef.current?.api?.refreshCells({
      columns: [TIME_COLUMN_ID],
      force: true,
    });
    incomingGridRef.current?.api?.refreshHeader();
    outgoingGridRef.current?.api?.refreshHeader();
  }, [useUtcTime]);

  const fetchIncomingData = useCallback(
    async (pageNumber = 1, append = false, search?: string) => {
      if (incomingLoadingRef.current) return;

      try {
        incomingLoadingRef.current = true;
        setIncomingLoading(true);
        const params: any = {
          page: pageNumber,
          limit: rowsPerPage,
          transactionType: incomingTransactionType,
          days: 7,
          userAccountType: isPaysonic ? "cashier" : undefined,
        };

        if (search && search.trim()) {
          const searchValue = search.trim();
          params.search = searchValue;
        }

        if (incomingPendingFilter) {
          params.statusId = 1;
        }

        if (incomingCompliancePendingFilter) {
          params.compliancePending = 1;
        }

        const response: GetUnifiedTransactionsResponse =
          await transactionApiServices.getUnifiedTransactions(params);

        if (append) {
          setIncomingData((prev) => [...prev, ...response.data.transactions]);
        } else {
          setIncomingData(response.data.transactions);
        }
        setIncomingHasMore(response.data.pagination.hasMore);
      } catch (err: any) {
        setError("Failed to fetch incoming transactions");
      } finally {
        incomingLoadingRef.current = false;
        setIncomingLoading(false);
      }
    },
    [
      incomingCompliancePendingFilter,
      incomingPendingFilter,
      incomingTransactionType,
    ],
  );

  const fetchOutgoingData = useCallback(
    async (pageNumber = 1, append = false, search?: string) => {
      if (outgoingLoadingRef.current) return;

      try {
        outgoingLoadingRef.current = true;
        setOutgoingLoading(true);
        const params: any = {
          page: pageNumber,
          limit: rowsPerPage,
          transactionType: outgoingTransactionType,
          days: 7,
          userAccountType: isPaysonic ? "cashier" : undefined,
        };

        if (search && search.trim()) {
          const searchValue = search.trim();
          params.search = searchValue;
        }

        if (outgoingPendingFilter) {
          params.statusId = 1;
        }

        if (outgoingCompliancePendingFilter) {
          params.compliancePending = 1;
        }

        const response: GetUnifiedTransactionsResponse =
          await transactionApiServices.getUnifiedTransactions(params);

        if (append) {
          setOutgoingData((prev) => [...prev, ...response.data.transactions]);
        } else {
          setOutgoingData(response.data.transactions);
        }
        setOutgoingHasMore(response.data.pagination.hasMore);
      } catch (err: any) {
        setError("Failed to fetch outgoing transactions");
      } finally {
        outgoingLoadingRef.current = false;
        setOutgoingLoading(false);
      }
    },
    [
      outgoingCompliancePendingFilter,
      outgoingPendingFilter,
      outgoingTransactionType,
    ],
  );

  const resetIncomingListState = useCallback(() => {
    setIncomingData([]);
    setIncomingPage(1);
    setIncomingHasMore(true);
  }, []);

  const resetOutgoingListState = useCallback(() => {
    setOutgoingData([]);
    setOutgoingPage(1);
    setOutgoingHasMore(true);
  }, []);

  useEffect(() => {
    resetIncomingListState();
    fetchIncomingData(1, false, appliedIncomingSearchTerm || undefined);
  }, [
    appliedIncomingSearchTerm,
    incomingCompliancePendingFilter,
    incomingPendingFilter,
    fetchIncomingData,
    resetIncomingListState,
  ]);

  useEffect(() => {
    resetOutgoingListState();
    fetchOutgoingData(1, false, appliedOutgoingSearchTerm || undefined);
  }, [
    appliedOutgoingSearchTerm,
    outgoingCompliancePendingFilter,
    outgoingPendingFilter,
    fetchOutgoingData,
    resetOutgoingListState,
  ]);

  const handleIncomingSearchChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setIncomingSearchTerm(e.target.value);
  };

  const handleOutgoingSearchChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setOutgoingSearchTerm(e.target.value);
  };

  const handleIncomingSearch = useCallback(() => {
    const normalizedSearchTerm = incomingSearchTerm.trim();

    if (normalizedSearchTerm === appliedIncomingSearchTerm) {
      resetIncomingListState();
      fetchIncomingData(1, false, normalizedSearchTerm || undefined);
      return;
    }

    setAppliedIncomingSearchTerm(normalizedSearchTerm);
  }, [
    appliedIncomingSearchTerm,
    fetchIncomingData,
    incomingSearchTerm,
    resetIncomingListState,
  ]);

  const handleOutgoingSearch = useCallback(() => {
    const normalizedSearchTerm = outgoingSearchTerm.trim();

    if (normalizedSearchTerm === appliedOutgoingSearchTerm) {
      resetOutgoingListState();
      fetchOutgoingData(1, false, normalizedSearchTerm || undefined);
      return;
    }

    setAppliedOutgoingSearchTerm(normalizedSearchTerm);
  }, [
    appliedOutgoingSearchTerm,
    fetchOutgoingData,
    outgoingSearchTerm,
    resetOutgoingListState,
  ]);

  const handleIncomingClearSearch = useCallback(() => {
    if (!incomingSearchTerm && !appliedIncomingSearchTerm) {
      return;
    }

    setIncomingSearchTerm("");

    if (appliedIncomingSearchTerm === "") {
      resetIncomingListState();
      fetchIncomingData(1, false);
      return;
    }

    setAppliedIncomingSearchTerm("");
  }, [
    appliedIncomingSearchTerm,
    fetchIncomingData,
    incomingSearchTerm,
    resetIncomingListState,
  ]);

  const handleOutgoingClearSearch = useCallback(() => {
    if (!outgoingSearchTerm && !appliedOutgoingSearchTerm) {
      return;
    }

    setOutgoingSearchTerm("");

    if (appliedOutgoingSearchTerm === "") {
      resetOutgoingListState();
      fetchOutgoingData(1, false);
      return;
    }

    setAppliedOutgoingSearchTerm("");
  }, [
    appliedOutgoingSearchTerm,
    fetchOutgoingData,
    outgoingSearchTerm,
    resetOutgoingListState,
  ]);

  const loadMoreIncomingData = useCallback(() => {
    if (!incomingLoadingRef.current && incomingHasMore) {
      const nextPage = incomingPage + 1;
      setIncomingPage(nextPage);
      fetchIncomingData(nextPage, true, appliedIncomingSearchTerm || undefined);
    }
  }, [
    incomingHasMore,
    incomingPage,
    appliedIncomingSearchTerm,
    fetchIncomingData,
  ]);

  const loadMoreOutgoingData = useCallback(() => {
    if (!outgoingLoadingRef.current && outgoingHasMore) {
      const nextPage = outgoingPage + 1;
      setOutgoingPage(nextPage);
      fetchOutgoingData(nextPage, true, appliedOutgoingSearchTerm || undefined);
    }
  }, [
    outgoingHasMore,
    outgoingPage,
    appliedOutgoingSearchTerm,
    fetchOutgoingData,
  ]);

  const onIncomingBodyScroll = useCallback(
    (params: any) => {
      if (!incomingHasMore || incomingLoading) return;

      const api = params.api;
      const displayedRowCount = api.getDisplayedRowCount();
      const lastDisplayedRowIndex = api.getLastDisplayedRowIndex();

      if (
        lastDisplayedRowIndex >= displayedRowCount - 5 &&
        displayedRowCount > 0
      ) {
        loadMoreIncomingData();
      }
    },
    [incomingHasMore, incomingLoading, loadMoreIncomingData],
  );

  const onOutgoingBodyScroll = useCallback(
    (params: any) => {
      if (!outgoingHasMore || outgoingLoading) return;

      const api = params.api;
      const displayedRowCount = api.getDisplayedRowCount();
      const lastDisplayedRowIndex = api.getLastDisplayedRowIndex();

      if (
        lastDisplayedRowIndex >= displayedRowCount - 5 &&
        displayedRowCount > 0
      ) {
        loadMoreOutgoingData();
      }
    },
    [outgoingHasMore, outgoingLoading, loadMoreOutgoingData],
  );

  const onRowClicked = (event: RowClickedEvent) => {
    const selectedData = event.data as UnifiedTransactionData;

    // Clear all selected rows from both grids
    const allRows = document.querySelectorAll(".ag-row");
    allRows.forEach((row) => {
      row.classList.remove("ag-row-selected-custom");
    });

    // Use the event's api to get the grid element directly
    if (event.rowIndex !== null) {
      // The event.api belongs to the specific grid that was clicked
      const gridElement =
        (event.api as any).gridCore?.eGridDiv || (event.api as any).eGridDiv;

      if (gridElement) {
        const rowElement = gridElement.querySelector(
          `[row-index="${event.rowIndex}"]`,
        );
        if (rowElement) {
          rowElement.classList.add("ag-row-selected-custom");
        }
      } else {
        // Fallback: Find the row element through the clicked row node
        const rowNode = event.node;
        if (rowNode) {
          const rowElement = document.querySelector(`[row-id="${rowNode.id}"]`);
          if (rowElement) {
            rowElement.classList.add("ag-row-selected-custom");
          }
        }
      }
    }

    setSelectedTransactionId(selectedData.transactionId);
    setSelectedTransactionData(selectedData);
    setIsPanelCollapsed(false);
  };

  return (
    <>
      <div className="h-[70px] border-b border-border-subtle bg-bg-page-body flex flex-row sticky top-0 bg-white z-10 pl-[20px] w-full">
        <div className="flex flex-grow justify-between items-center w-full mr-4">
          <p className="flex text-text-base text-[14px] font-semibold items-center">
            {pageTitle}
          </p>
        </div>
      </div>
      <div className="flex flex-row items-start">
        <div
          className={`flex flex-col bg-bg-page-body py-[20px] px-[12px] overflow-hidden transition-all duration-300 ${
            selectedTransactionId && !isPanelCollapsed ? "w-3/4" : "w-full"
          }`}
          style={{ height: "calc(100dvh - 70px)" }}
        >
          <div className="flex flex-col h-full gap-4">
            <div className="flex flex-col h-1/2 min-h-0">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <div className="bg-bg-button-secondary border border-border-subtle rounded-lg px-3 py-1.5">
                    <p className="text-text-base text-[12px] font-bold">
                      Incoming
                    </p>
                  </div>
                  <p className="text-text-base text-[10px] font-semibold">
                    Filter by:
                  </p>
                  <div
                    onClick={
                      incomingLoading
                        ? undefined
                        : () => setIncomingPendingFilter(!incomingPendingFilter)
                    }
                    className={`border py-[2px] px-[12px] text-text-base text-[10px] font-medium rounded-[100px] ${
                      incomingLoading
                        ? "opacity-50 cursor-not-allowed"
                        : "cursor-pointer"
                    } ${
                      incomingPendingFilter
                        ? "border-border-button-secondary bg-bg-button-secondary"
                        : "border-border-subtle bg-bg-page-body"
                    }`}
                  >
                    Pending
                  </div>
                  <div
                    style={{
                      height: "20px",
                      width: "2px",
                      backgroundColor: "#ccc",
                    }}
                  />
                  <div
                    onClick={
                      incomingLoading
                        ? undefined
                        : () =>
                            setIncomingCompliancePendingFilter((prev) => !prev)
                    }
                    className={`border py-[2px] px-[12px] text-text-base text-[10px] font-medium rounded-[100px] ${
                      incomingLoading
                        ? "opacity-50 cursor-not-allowed"
                        : "cursor-pointer"
                    } ${
                      incomingCompliancePendingFilter
                        ? "border-border-button-secondary bg-bg-button-secondary"
                        : "border-border-subtle bg-bg-page-body"
                    }`}
                  >
                    Compliance
                  </div>
                </div>
                <div className="w-[300px] flex-shrink-0">
                  <GlobalSearchInputField
                    value={incomingSearchTerm}
                    onChange={handleIncomingSearchChange}
                    onSearch={handleIncomingSearch}
                    onClear={handleIncomingClearSearch}
                    disabled={incomingLoading}
                    isSearchApplied={Boolean(appliedIncomingSearchTerm)}
                    placeholder="Search by User ID / Merchant ID / Transaction ID"
                  />
                </div>
              </div>
              {incomingLoading && incomingData.length === 0 ? (
                <AgGridSkeleton
                  columnDefs={columnDefs}
                  style={{ height: "100%", width: "100%" }}
                />
              ) : (
                <AgGridReactTable
                  ref={incomingGridRef}
                  rowData={incomingData}
                  columnDefs={columnDefs}
                  onRowClicked={onRowClicked}
                  onBodyScroll={onIncomingBodyScroll}
                  getRowId={(params) => `incoming-${params.data.transactionId}`}
                  error={error}
                  style={{ height: "100%" }}
                />
              )}
            </div>

            <div className="flex flex-col h-1/2 min-h-0">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <div className="bg-bg-button-secondary border border-border-subtle rounded-lg px-3 py-1.5">
                    <p className="text-text-base text-[12px] font-bold">
                      Outgoing
                    </p>
                  </div>
                  <p className="text-text-base text-[10px] font-semibold">
                    Filter by:
                  </p>
                  <div
                    onClick={
                      outgoingLoading
                        ? undefined
                        : () => setOutgoingPendingFilter(!outgoingPendingFilter)
                    }
                    className={`border py-[2px] px-[12px] text-text-base text-[10px] font-medium rounded-[100px] ${
                      outgoingLoading
                        ? "opacity-50 cursor-not-allowed"
                        : "cursor-pointer"
                    } ${
                      outgoingPendingFilter
                        ? "border-border-button-secondary bg-bg-button-secondary"
                        : "border-border-subtle bg-bg-page-body"
                    }`}
                  >
                    Pending
                  </div>
                  <div
                    style={{
                      height: "20px",
                      width: "2px",
                      backgroundColor: "#ccc",
                    }}
                  />
                  <div
                    onClick={
                      outgoingLoading
                        ? undefined
                        : () =>
                            setOutgoingCompliancePendingFilter((prev) => !prev)
                    }
                    className={`border py-[2px] px-[12px] text-text-base text-[10px] font-medium rounded-[100px] ${
                      outgoingLoading
                        ? "opacity-50 cursor-not-allowed"
                        : "cursor-pointer"
                    } ${
                      outgoingCompliancePendingFilter
                        ? "border-border-button-secondary bg-bg-button-secondary"
                        : "border-border-subtle bg-bg-page-body"
                    }`}
                  >
                    Compliance
                  </div>
                </div>
                <div className="w-[300px] flex-shrink-0">
                  <GlobalSearchInputField
                    value={outgoingSearchTerm}
                    onChange={handleOutgoingSearchChange}
                    onSearch={handleOutgoingSearch}
                    onClear={handleOutgoingClearSearch}
                    disabled={outgoingLoading}
                    isSearchApplied={Boolean(appliedOutgoingSearchTerm)}
                    placeholder="Search by User ID / Merchant ID / Transaction ID"
                  />
                </div>
              </div>
              {outgoingLoading && outgoingData.length === 0 ? (
                <AgGridSkeleton
                  columnDefs={columnDefs}
                  style={{ height: "100%", width: "100%" }}
                />
              ) : (
                <AgGridReactTable
                  ref={outgoingGridRef}
                  rowData={outgoingData}
                  columnDefs={columnDefs}
                  onRowClicked={onRowClicked}
                  onBodyScroll={onOutgoingBodyScroll}
                  getRowId={(params) => `outgoing-${params.data.transactionId}`}
                  error={error}
                  style={{ height: "100%" }}
                />
              )}
            </div>
          </div>
        </div>

        {selectedTransactionId && selectedTransactionData && (
          <div
            className={`bg-bg-page-body py-4 border-l border-border-subtle transition-all duration-300 relative ${
              isPanelCollapsed ? "w-0 px-0" : "w-1/4 px-2"
            }`}
            style={{ height: "calc(100dvh - 70px)" }}
          >
            <button
              onClick={() => setIsPanelCollapsed(!isPanelCollapsed)}
              className="absolute top-1/2 -translate-y-1/2 z-20 bg-bg-button-secondary text-text-base rounded-lg shadow-lg transition-all duration-200 flex items-center justify-center group"
              style={{
                left: isPanelCollapsed ? "calc(100% - 32px)" : "-16px",
                width: "32px",
                height: "60px",
              }}
              aria-label={isPanelCollapsed ? "Expand panel" : "Collapse panel"}
            >
              <span className="material-symbols-outlined text-[20px] group-hover:scale-110 transition-transform">
                {isPanelCollapsed ? "chevron_left" : "chevron_right"}
              </span>
            </button>

            <div
              className={`overflow-auto h-full transition-opacity duration-300 ${
                isPanelCollapsed
                  ? "opacity-0 pointer-events-none"
                  : "opacity-100"
              }`}
            >
              <UnifiedTransactionTabs
                transactionId={selectedTransactionId}
                merchantId={
                  selectedTransactionData?.accountType === "MERCHANT"
                    ? selectedTransactionData?.merchantId!
                    : undefined
                }
                ledger={selectedTransactionData?.ledger}
                merchantOrderId={selectedTransactionData?.merchantOrderId}
              />
            </div>
          </div>
        )}
      </div>
      <ConfirmClearComplianceModal
        open={clearComplianceTransactionId != null}
        transactionId={clearComplianceTransactionId ?? undefined}
        onClose={() => setClearComplianceTransactionId(null)}
        onSuccess={(transactionId) => {
          const markCleared = (rows: UnifiedTransactionData[]) =>
            rows.map((row) =>
              row.transactionId === transactionId
                ? { ...row, compliancePending: 0 }
                : row,
            );
          setIncomingData(markCleared);
          setOutgoingData(markCleared);
          setSelectedTransactionData((current) =>
            current?.transactionId === transactionId
              ? { ...current, compliancePending: 0 }
              : current,
          );
        }}
      />
    </>
  );
};

export default UnifiedTransactions;
